import { useState, useMemo, useEffect, useRef } from 'react'
import { Box, Typography, Stack, Button } from '@mui/material'
import Panel from '../../../components/common/Panel'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import { FS, SP, PANEL_SX, RAIL_WIDTH } from '../../../theme/tokens'
import TimelineIcon from './TimelineIcon'
import { EVENT_TYPES, DEFAULT_PALETTE, SEVERITY_TINT, resolvePalette } from './palette'
import EmptyState from '../EmptyState'

export { EVENT_TYPES, DEFAULT_PALETTE, SEVERITY_TINT, resolvePalette }

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

function formatGap(deltaMs) {
  const abs = Math.abs(deltaMs)
  if (abs >= DAY_MS) {
    const days = Math.round(abs / DAY_MS)
    return days === 1 ? '1 day later' : `${days} days later`
  }
  const hours = Math.round(abs / HOUR_MS)
  return hours === 1 ? '1 hour later' : `${hours} hours later`
}

/**
 * ActivityTimeline — generic, powerful activity / communications stream.
 *
 * @description
 *   Renders a vertical MUI-Lab Timeline of entries with:
 *     • Consumer-injected icon + content renderers
 *     • Optional tab filter bar (Relevant / All, extensible)
 *     • Optional duration-gap pseudo-events between entries far apart in time
 *     • Optional severity tinting (error / warning / info / success)
 *     • Shared event-type palette (status_change / email_* / payment /
 *       shipping / fx / invoice / error / …) so every consumer shares a
 *       single visual vocabulary
 *     • Pinned events — always visible regardless of active tab filter
 *     • Paper wrapper and empty state
 *   Domain-specific concerns (entry shape, relevance filter, content layout)
 *   still live in the consumer's adapter. Same component serves RMA + PO.
 *
 * @useWhen
 *   - Case / order / entity detail page needs a chronological activity stream
 *   - Multiple event sources merge (emails, status changes, notes, webhooks,
 *     data change-logs) into one view
 *   - Tab filters / duration separators / severity highlighting needed
 *
 * @dontUseFor
 *   - Simple list of events with no icons or tabs → plain `List`
 *   - Kanban / board view → a Board component
 *   - Read-only audit log in admin table → `DataGrid`
 *
 * @related
 *   - TimelineIcon — circle wrapper used by iconFor or the default palette
 *   - EVENT_TYPES / DEFAULT_PALETTE — shared visual vocabulary exports
 *
 * @reuseHints
 *   - PO detail activity stream (F.4) — same component, PO-specific adapter
 *   - Vendor profile cross-case timeline
 *   - Discovery candidate audit trail
 *   - FCA allocation change log
 *
 * @props
 *   entries — array of { id, date (ISO or Date), type, ... }. `type`
 *     drives the default palette if `iconFor` returns null.
 *   iconFor(entry) — optional. Return a ReactNode (usually <TimelineIcon>)
 *     or null to fall back to the shared palette for `entry.type`.
 *   renderEntry(entry) — required. Body inside TimelineContent.
 *   filters — optional: { tabs: [{value, label, match?(entry), showCount?}],
 *     default: 'relevant' }. Safe tabs (no match) show all entries.
 *     The bar is a WAI-ARIA tablist: one roving tab stop on the active tab,
 *     ArrowLeft/ArrowRight move focus AND selection (wrapping), Home/End
 *     jump to the ends, Enter/Space select. Inactive tabs carry tabIndex
 *     -1, so without those keys they would be unreachable by keyboard.
 *   pinnedFor(entry) — optional. Returning true pins the entry so it shows
 *     on every tab regardless of the active tab's match filter (useful
 *     for errors / blockers that must never hide).
 *   severityFor(entry) — optional. Return 'error'|'warning'|'info'|
 *     'success'|null. Non-null tints the TimelineContent row.
 *   showDurationGaps — when true, insert pseudo-entries with
 *     type=EVENT_TYPES.DURATION_GAP between adjacent real entries whose
 *     time delta exceeds `durationGapThresholdHours` (default 24).
 *   durationGapThresholdHours — default 24. Set lower for chattier
 *     contexts (e.g. 6 for RMA), higher for longer-running flows.
 *   title — heading inside the Paper wrapper.
 *   disablePaper — render without outer Paper (e.g. embedded in another card).
 *   emptyLabel / emptyLabels — fallback text; emptyLabels can provide a
 *     per-tab override.
 *   paperSx — sx merged into the outer Paper.
 *   sortKey — custom entry comparator; defaults to newest-first by date.
 */
export default function ActivityTimeline({
  entries = [],
  iconFor,
  renderEntry,
  filters = null,
  pinnedFor = null,
  severityFor = null,
  showDurationGaps = false,
  durationGapThresholdHours = 24,
  title = 'Activity',
  disablePaper = false,
  emptyLabel = 'No activity yet.',
  emptyLabels = null,
  paperSx = PANEL_SX,
  sortKey = null,
  // ── Optional cap (§5.3 of docs/forge-ux-redesign/concepts/cases.md) ───────
  //
  // 🔴 OPT-IN, default `null` = no cap. This component is shared (the RMA case
  // detail and the PO detail both mount it), so a default cap would silently
  // change a surface that did not ask for one. The caller that has the problem
  // — a case with hundreds of events rendering hundreds of rows in one pass —
  // passes the numbers; everyone else keeps today's behaviour byte for byte.
  //
  // The shape is copied from `ConversationStream`, which solved the same
  // problem for the sibling tab: an initial window plus a reveal in batches.
  initialVisible = null,
  revealBatch = 8,
}) {
  const sortedEntries = useMemo(() => {
    const arr = entries.slice()
    if (sortKey) arr.sort(sortKey)
    else arr.sort((a, b) => new Date(b.date) - new Date(a.date))
    return arr
  }, [entries, sortKey])

  const tabs = filters?.tabs || null
  const defaultTab = filters?.default || (tabs?.[0]?.value ?? null)
  const [tab, setTab] = useState(defaultTab)

  const activeTab = tabs ? tabs.find(t => t.value === tab) : null

  // Filter by active tab, but always include pinned entries (overrides tab).
  const visibleEntries = useMemo(() => {
    if (!tabs || !activeTab || !activeTab.match) return sortedEntries
    return sortedEntries.filter(e => {
      if (pinnedFor && pinnedFor(e)) return true
      return activeTab.match(e)
    })
  }, [sortedEntries, tabs, activeTab, pinnedFor])

  // Insert duration-gap pseudo-entries between adjacent visible entries
  // whose time delta exceeds the threshold. Pseudo-entries are not real
  // activity — they render as a simple "N days later" separator row.
  const withGaps = useMemo(() => {
    if (!showDurationGaps || visibleEntries.length < 2) return visibleEntries
    const thresholdMs = durationGapThresholdHours * HOUR_MS
    const out = []
    for (let i = 0; i < visibleEntries.length; i++) {
      out.push(visibleEntries[i])
      const next = visibleEntries[i + 1]
      if (!next) continue
      const delta = new Date(visibleEntries[i].date) - new Date(next.date)
      if (Math.abs(delta) < thresholdMs) continue
      out.push({
        id: `_gap-${visibleEntries[i].id ?? i}`,
        type: EVENT_TYPES.DURATION_GAP,
        date: new Date((new Date(visibleEntries[i].date).getTime() + new Date(next.date).getTime()) / 2).toISOString(),
        _gapLabel: formatGap(delta),
      })
    }
    return out
  }, [visibleEntries, showDurationGaps, durationGapThresholdHours])

  // WAI-ARIA tabs pattern: one roving tab stop (the active tab), and the
  // arrow keys move BOTH focus and selection — wrapping at both ends. Without
  // them a `role="tab"` bar with `tabIndex={-1}` on the inactive tabs is
  // reachable only on its active tab, i.e. the tab pattern without its keys.
  const tabValues = tabs ? tabs.map(t => t.value) : []
  const tabRefs = useRef({})
  const focusTab = (index) => {
    const n = tabValues.length
    if (!n) return
    const value = tabValues[((index % n) + n) % n]
    setTab(value)
    const node = tabRefs.current[value]
    if (node && typeof node.focus === 'function') node.focus()
  }

  const tabBar = tabs && (
    <Stack direction="row" gap={0} role="tablist" aria-label="Activity filter"
      sx={{ borderBottom: '2px solid', borderColor: 'divider', mb: SP.sm }}>
      {tabs.map((t, index) => {
        const isActive = t.value === tab
        const count = t.showCount ? sortedEntries.length : null
        return (
          <Box
            key={t.value}
            ref={(node) => { tabRefs.current[t.value] = node }}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => setTab(t.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                e.preventDefault()
                focusTab(index + 1)
              } else if (e.key === 'ArrowLeft') {
                e.preventDefault()
                focusTab(index - 1)
              } else if (e.key === 'Home') {
                e.preventDefault()
                focusTab(0)
              } else if (e.key === 'End') {
                e.preventDefault()
                focusTab(tabValues.length - 1)
              } else if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setTab(t.value)
              }
            }}
            sx={{
              px: SP.sm, py: 0.75, cursor: 'pointer',
              fontSize: FS.md, fontWeight: isActive ? 600 : 400,
              color: isActive ? 'primary.main' : 'text.secondary',
              borderBottom: '2px solid',
              borderColor: isActive ? 'primary.main' : 'transparent',
              mb: '-2px',
              userSelect: 'none',
              display: 'flex', alignItems: 'center', gap: 0.75,
            }}
          >
            {t.label}
            {count != null && (
              <Box
                component="span"
                sx={{
                  fontSize: FS.xs, bgcolor: 'action.hover', borderRadius: 99,
                  px: 0.75, py: 0.125, lineHeight: 1.4,
                }}
              >
                {count}
              </Box>
            )}
          </Box>
        )
      })}
    </Stack>
  )

  const capped = typeof initialVisible === 'number' && initialVisible > 0
  const [shown, setShown] = useState(capped ? initialVisible : withGaps.length)
  // A new tab is a new list. Without this reset, switching from a 3-entry tab
  // to a 400-entry one would keep whatever reveal count the first tab had.
  useEffect(() => {
    setShown(capped ? initialVisible : withGaps.length)
  }, [tab, capped, initialVisible, withGaps.length])

  const visibleGaps = capped ? withGaps.slice(0, shown) : withGaps
  const hiddenCount = withGaps.length - visibleGaps.length

  const empty = withGaps.length === 0
  const emptyText = (emptyLabels && tab && emptyLabels[tab]) || emptyLabel

  const renderIcon = (entry) => {
    const fromConsumer = iconFor ? iconFor(entry) : null
    if (fromConsumer) return fromConsumer
    const pal = resolvePalette(entry)
    return <TimelineIcon bg={pal.bg} border={pal.border} icon={<pal.Icon sx={{ fontSize: 14, color: pal.border }} />} />
  }

  const renderBody = (entry) => {
    // Pseudo-entry: duration gap
    if (entry.type === EVENT_TYPES.DURATION_GAP) {
      return (
        <Typography sx={{ fontSize: FS.xs, color: 'text.disabled', fontStyle: 'italic', py: 0.5 }}>
          {entry._gapLabel}
        </Typography>
      )
    }
    const body = renderEntry ? renderEntry(entry) : null
    const sev = severityFor ? severityFor(entry) : null
    if (sev && SEVERITY_TINT[sev]) {
      const tint = SEVERITY_TINT[sev]
      return (
        <Box sx={{
          bgcolor: tint.bg,
          borderLeft: `${RAIL_WIDTH}px solid`,
          borderLeftColor: tint.border,
          pl: 1.25, pr: 1, py: 0.75,
          borderRadius: 0.5,
        }}>
          {body}
        </Box>
      )
    }
    return body
  }

  const inner = (
    <>
      {tabBar}
      {empty ? (
        // Der Grund kommt aus dem Reiter, nicht aus dem Bauch: filtert der
        // aktive Reiter (z. B. „Relevant"), ist die Leere ein Nichttreffer
        // (no-match); ohne Filter ist wirklich nichts da (no-data).
        <EmptyState reason={activeTab?.match ? 'no-match' : 'no-data'} message={emptyText} />
      ) : (
        <Timeline sx={{ p: 0, m: 0 }}>
          {visibleGaps.map((entry, i) => (
            <TimelineItem
              key={entry.id ?? `${entry.kind || ''}-${i}`}
              sx={{ '&:before': { flex: 0, padding: 0 } }}
            >
              <TimelineSeparator>
                {renderIcon(entry)}
                {i < visibleGaps.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent sx={{ pb: SP.card, minWidth: 0 }}>
                {renderBody(entry)}
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
      {/* The count is printed, not implied. "Show more" alone leaves the
          reader guessing whether two rows or two hundred are behind it, and a
          long audit trail is exactly where that guess is expensive. */}
      {!empty && hiddenCount > 0 && (
        <Button
          size="small"
          onClick={() => setShown(n => n + revealBatch)}
          sx={{ mt: 0.5 }}
        >
          {`Show ${Math.min(revealBatch, hiddenCount)} more (${hiddenCount} hidden)`}
        </Button>
      )}
    </>
  )

  if (disablePaper) return <Box sx={{ pt: SP.sm }}>{inner}</Box>

  return (
    <Panel sx={paperSx}>
      <Typography variant="subtitle2" mb={SP.sm}>{title}</Typography>
      {inner}
    </Panel>
  )
}
